import type { ImageSourcePropType } from "react-native";
import {
  getTestingMethodsForMarker,
  type BiomarkerTestingMethod,
} from "@src/packages/content";
import type { ResultInterpretationContext } from "@src/packages/test-results/interpretation-context";
import { FR_METRIC_FACTSHEETS } from "./metricFactsheets.fr";

/**
 * The shape of a metric factsheet.
 *
 * These lived beside the metric detail sheet, which is gone: the `MetricDetail`
 * route now hands every metric to its canonical test detail. The factsheets
 * still feed that redirect and the Tests catalogue, and the cycle link is the
 * shape Care's result rows pass when they open a cycle.
 */
export type MetricHistoryPoint = {
  day: string;
  date: string;
  value: string;
};

export type MetricChartPoint = {
  label: string;
  sublabel?: string;
  value: number;
};

export type MetricChartSeries = {
  label: string;
  color?: string;
  cycleLink?: MetricCycleLink;
  points: MetricChartPoint[];
};

export type MetricRelatedDocument = {
  id: string;
  title: string;
  subtitle?: string;
  date?: string;
};

export type MetricTestingMethod = BiomarkerTestingMethod;

export type MetricCycleLink = {
  label: string;
  cycleId?: string | null;
  isPast?: boolean;
};

export type ResponseMetric = {
  label: string;
  current?: string;
  unit?: string;
  previous?: string;
  trend: "up" | "down" | "stable";
  context?: string;
  contextGood?: boolean;
  description: string;
  whyItMatters: string;
  normalRange: string;
  history: MetricHistoryPoint[];
  valueMode?: "cycle" | "lifetime";
  chartSeries?: MetricChartSeries[];
  imageSource?: ImageSourcePropType;
  relatedDocuments?: MetricRelatedDocument[];
  cycleLink?: MetricCycleLink;
  testingMethods?: MetricTestingMethod[];
  interpretationContext?: ResultInterpretationContext;
};

export type MetricFactsheet = {
  key: string;
  metric: ResponseMetric;
  keywords: string;
};

function withTestingMethods(metric: ResponseMetric): ResponseMetric {
  const testingMethods = getTestingMethodsForMarker(metric.label);
  if (!testingMethods.length) return metric;
  return { ...metric, testingMethods };
}

const EN_METRIC_FACTSHEETS: MetricFactsheet[] = [
  {
    key: "estradiol",
    keywords: "e2 estrogen stimulation blood hormone ovarian response",
    metric: {
      label: "Estradiol",
      current: "1280",
      unit: "pg/mL",
      previous: "840",
      trend: "up",
      context: "Rising",
      contextGood: true,
      cycleLink: { label: "Current cycle" },
      description:
        "Estradiol is an estrogen hormone produced by growing follicles during ovarian stimulation.",
      whyItMatters:
        "Clinics use estradiol trends together with ultrasound to understand follicle activity and adjust stimulation safely.",
      normalRange:
        "There is no single target. During stimulation, estradiol usually rises as follicles grow, and very high levels may change the protocol.",
      history: [
        { day: "Day 5", date: "Jun 4", value: "410" },
        { day: "Day 8", date: "Jun 7", value: "840" },
        { day: "Day 10", date: "Jun 9", value: "1280" },
      ],
      valueMode: "cycle",
      chartSeries: [
        {
          label: "Current cycle",
          cycleLink: { label: "Current cycle" },
          points: [
            { label: "D5", sublabel: "Jun 4", value: 410 },
            { label: "D8", sublabel: "Jun 7", value: 840 },
            { label: "D10", sublabel: "Jun 9", value: 1280 },
          ],
        },
        {
          label: "IVF cycle number two",
          cycleLink: { label: "IVF cycle number two", isPast: true },
          color: "#9A8E85",
          points: [
            { label: "D5", sublabel: "Mar 3", value: 360 },
            { label: "D8", sublabel: "Mar 6", value: 790 },
            { label: "D10", sublabel: "Mar 8", value: 1110 },
          ],
        },
      ],
    },
  },
  {
    key: "follicles",
    keywords:
      "follicle count follicle sizes ovarian follicles ultrasound monitoring afc response egg retrieval",
    metric: {
      label: "Follicles",
      imageSource: require("../../assets/images/follicles-ovary.png"),
      current: "12",
      unit: "total",
      previous: "8",
      trend: "up",
      context: "Good response",
      contextGood: true,
      cycleLink: { label: "Current cycle" },
      description:
        "Follicle count tracks how many measurable follicles are seen on ultrasound during stimulation.",
      whyItMatters:
        "The number and size pattern helps your clinic time monitoring, medication changes, and the trigger shot.",
      normalRange:
        "Expected counts vary by age, reserve, and protocol. Your team interprets the count with follicle sizes and blood work.",
      history: [
        { day: "Day 5", date: "Jun 4", value: "5" },
        { day: "Day 8", date: "Jun 7", value: "8" },
        { day: "Day 10", date: "Jun 9", value: "12" },
      ],
      valueMode: "cycle",
      chartSeries: [
        {
          label: "Current cycle",
          cycleLink: { label: "Current cycle" },
          points: [
            { label: "D5", sublabel: "Jun 4", value: 5 },
            { label: "D8", sublabel: "Jun 7", value: 8 },
            { label: "D10", sublabel: "Jun 9", value: 12 },
          ],
        },
        {
          label: "Egg freezing cycle number one",
          cycleLink: { label: "Egg freezing cycle number one", isPast: true },
          color: "#9A8E85",
          points: [
            { label: "D5", sublabel: "Feb 2", value: 4 },
            { label: "D8", sublabel: "Feb 5", value: 7 },
            { label: "D10", sublabel: "Feb 7", value: 10 },
          ],
        },
      ],
      relatedDocuments: [
        {
          id: "follicles-us-1",
          title: "Monitoring ultrasound",
          subtitle: "Follicle count and sizes",
          date: "Jun 9, 2026",
        },
        {
          id: "follicles-us-2",
          title: "Previous cycle ultrasound",
          subtitle: "Egg freezing monitoring",
          date: "Feb 7, 2026",
        },
      ],
    },
  },
  {
    key: "lining",
    keywords:
      "endometrial lining uterus uterine lining thickness trilaminar ultrasound transfer",
    metric: {
      label: "Endometrial lining",
      current: "8.2",
      unit: "mm",
      previous: "6.8",
      trend: "up",
      context: "Optimal",
      contextGood: true,
      cycleLink: { label: "Current cycle" },
      description:
        "Endometrial lining is the thickness of the uterine lining measured on ultrasound.",
      whyItMatters:
        "For transfer cycles, lining thickness and pattern help assess whether the uterus is ready for embryo transfer.",
      normalRange:
        "Many clinics look for roughly 7 mm or more before transfer, but pattern, timing, and protocol also matter.",
      history: [
        { day: "Day 5", date: "Jun 4", value: "5.4" },
        { day: "Day 8", date: "Jun 7", value: "6.8" },
        { day: "Day 10", date: "Jun 9", value: "8.2" },
      ],
      valueMode: "cycle",
    },
  },
  {
    key: "lh",
    keywords: "luteinizing hormone lh surge ovulation antagonist monitoring",
    metric: {
      label: "LH",
      current: "3.8",
      unit: "mIU/mL",
      previous: "4.2",
      trend: "down",
      context: "Suppressed",
      contextGood: true,
      cycleLink: { label: "Current cycle" },
      description:
        "Luteinizing hormone helps trigger ovulation naturally and is monitored during some treatment cycles.",
      whyItMatters:
        "A premature LH surge can mean ovulation is starting before retrieval or before a planned protocol step.",
      normalRange:
        "Targets depend on the protocol. During antagonist cycles, clinics often want LH to remain suppressed until trigger.",
      history: [
        { day: "Day 5", date: "Jun 4", value: "4.8" },
        { day: "Day 8", date: "Jun 7", value: "4.2" },
        { day: "Day 10", date: "Jun 9", value: "3.8" },
      ],
      valueMode: "cycle",
    },
  },
  {
    key: "progesterone",
    keywords: "progesterone p4 luteal premature luteinization trigger transfer",
    metric: {
      label: "Progesterone",
      current: "0.8",
      unit: "ng/mL",
      previous: "0.6",
      trend: "up",
      context: "Within range",
      contextGood: true,
      cycleLink: { label: "Current cycle" },
      description:
        "Progesterone helps prepare the uterine lining after ovulation, but timing matters during stimulation and transfer cycles.",
      whyItMatters:
        "A rise too early in stimulation can affect retrieval or transfer planning, so clinics may monitor it close to trigger.",
      normalRange:
        "During many antagonist stimulation protocols, progesterone is expected to stay low until after trigger.",
      history: [
        { day: "Day 5", date: "Jun 4", value: "0.4" },
        { day: "Day 8", date: "Jun 7", value: "0.6" },
        { day: "Day 10", date: "Jun 9", value: "0.8" },
      ],
      valueMode: "cycle",
    },
  },
  {
    key: "vitamin-d",
    keywords:
      "vitamin d 25-oh vitamin d nutrient deficiency supplementation biomarker",
    metric: {
      label: "Vitamin D",
      imageSource: require("../../assets/images/vitamin-d-icon.png"),
      current: "34",
      unit: "ng/mL",
      previous: "24",
      trend: "up",
      context: "Improved",
      contextGood: true,
      description:
        "Vitamin D is a fat-soluble vitamin that supports bone health, immune function, and several reproductive hormone pathways.",
      whyItMatters:
        "Low vitamin D is common and can be corrected over time. Your clinician may track it with other nutrition, thyroid, and metabolic markers.",
      normalRange:
        "Many labs consider 30 to 50 ng/mL sufficient, but targets vary by clinic, medical history, and supplementation plan.",
      history: [
        { day: "Jan", date: "2025", value: "18" },
        { day: "May", date: "2025", value: "24" },
        { day: "Nov", date: "2025", value: "31" },
        { day: "Jun", date: "2026", value: "34" },
      ],
      valueMode: "lifetime",
      chartSeries: [
        {
          label: "Lifetime trend",
          points: [
            { label: "Jan 25", value: 18 },
            { label: "May 25", value: 24 },
            { label: "Nov 25", value: 31 },
            { label: "Jun 26", value: 34 },
          ],
        },
      ],
      relatedDocuments: [
        {
          id: "vitd-lab-1",
          title: "Comprehensive biomarker panel",
          subtitle: "Vitamin D, thyroid, metabolic markers",
          date: "Jun 12, 2026",
        },
      ],
    },
  },
  {
    key: "amh",
    keywords: "amh anti mullerian hormone ovarian reserve fertility marker",
    metric: {
      label: "AMH",
      current: undefined,
      unit: "ng/mL",
      previous: undefined,
      trend: "stable",
      context: undefined,
      contextGood: true,
      description:
        "AMH is a hormone made by small ovarian follicles and is often used as one marker of ovarian reserve.",
      whyItMatters:
        "AMH can help estimate expected response to stimulation, but it does not directly measure egg quality or guarantee fertility outcomes.",
      normalRange:
        "Ranges vary by age and lab. Your clinician interprets AMH alongside AFC, age, history, and treatment goals.",
      history: [],
      valueMode: "lifetime",
      relatedDocuments: [],
    },
  },
];

function decorate(entries: MetricFactsheet[]): MetricFactsheet[] {
  return entries.map((entry) => ({
    ...entry,
    metric: withTestingMethods(entry.metric),
  }));
}

const EN_DECORATED = decorate(EN_METRIC_FACTSHEETS);
const FR_DECORATED = decorate(FR_METRIC_FACTSHEETS);

function pickFactsheets(locale: string): MetricFactsheet[] {
  return locale.startsWith("fr") ? FR_DECORATED : EN_DECORATED;
}

export function getMetricFactsheets(locale: string): MetricFactsheet[] {
  return pickFactsheets(locale);
}

export function getMetricsByKey(
  locale: string,
): Record<string, ResponseMetric> {
  return Object.fromEntries(
    pickFactsheets(locale).map((entry) => [entry.key, entry.metric]),
  );
}
